import { T } from 'src/shared/helpers';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { isPrintableComposerMesh } from '../../../configurator-composer/composer/helpers/composerAttributeValidation';
import { ComposerAttributeNodeAdapter, ComposerTemplateNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import { ComposerAreaNode, isComposerAreaLinkedToAttribute } from './composerAttributesModels';

// AGGIUNGERE UN FIGLIO SI FA DALLA RIGA (utente, 2026-10-15; prototipo, il «+» sulla riga). Prima un'opzione
// nasceva dal «+ Add option» dell'Inspector dell'attributo e un'area da «Add area»: adesso nascono dal «+»
// della riga nell'albero, e l'Inspector non ha più quel bottone. La logica sta qui, una volta sola, e legge
// lo store al momento del clic: la riga premuta non è per forza quella scelta.

/** Una nuova opzione in coda all'attributo: scelta e aperta nelle sue Settings, per darle subito un nome. */
export const addComposerOption = (attributeID: string) => {
    const store = useComposerStore.getState();
    const attribute = store.attributes.find((candidate) => candidate.id === attributeID);
    if (!attribute) return;
    const options = attribute.options ?? [];
    const option = ComposerAttributeNodeAdapter.toNodeOption(attribute.id, ComposerAttributeNodeAdapter.createNewOption(options.length));
    const names = new Set(options.map((candidate) => candidate.name));
    let suffix = 1;
    let name = T._('New option', 'Admin');
    while (names.has(name)) name = `${T._('New option', 'Admin')} ${++suffix}`;
    const created = {
        ...option,
        name,
        description: '',
        attributeID: attribute.id,
        displayOrder: options.length,
        isDefault: options.length === 0,
        isNew: true
    };
    store.updateOption(created);
    const owner = useComposerStore.getState().attributes.find((candidate) => candidate.id === attribute.id) ?? attribute;
    store.selectAttributes([owner]);
    store.selectAreas(null);
    store.selectOptions([created]);
    store.setOptionFace('settings');
    void store.validateAttributes();
};

/**
 * Una nuova area dell'attributo «Customizable area», sul primo oggetto stampabile che non ha già un'area.
 * Scelta: l'Inspector apre i suoi dettagli. `isRealMesh` è quello dello Scene Editor del palco: lo store del 3D
 * vive in un contesto React, e chi chiama ce l'ha in mano.
 */
export const addComposerArea = (attributeID: string, isRealMesh?: (mesh: BABYLON.AbstractMesh) => boolean, name?: string) => {
    const store = useComposerStore.getState();
    const attribute = store.attributes.find((candidate) => candidate.id === attributeID);
    if (!attribute) return;
    const linkedAreas = store.areas.filter((area) => isComposerAreaLinkedToAttribute(area, attribute));
    const parsed = ComposerTemplateNodeAdapter.fromAreaNode(null);
    const assignedMeshIDs = new Set(store.areas.flatMap((area) => area.objects.split(';').filter(Boolean)));
    const firstMesh = store.sceneObjectList.find((mesh) => !!isRealMesh?.(mesh) && isPrintableComposerMesh(mesh) && !assignedMeshIDs.has(mesh.id));
    const draft = ComposerTemplateNodeAdapter.toNodeArea(parsed.side, parsed.modelSides);
    Object.assign(draft, {
        name: name ?? T._(`Area ${linkedAreas.length + 1}`, 'Admin'),
        attributeID: attribute.idNumeric,
        attributeGuid: attribute.id,
        displayOrder: linkedAreas.length,
        width: 10,
        height: 10,
        ppcm: 102.4,
        camera: attribute.camera || '',
        objects: firstMesh?.id || ''
    });
    const hydrated = ComposerTemplateNodeAdapter.fromAreaNode(draft);
    const area: ComposerAreaNode = {
        ...ComposerTemplateNodeAdapter.toNodeArea(hydrated.side, hydrated.modelSides),
        ...draft,
        originalSide: hydrated.side,
        originalMeshSides: hydrated.modelSides.toArray()
    };
    store.updateArea(area);
    store.selectAttributes([attribute]);
    store.selectOptions(null);
    store.selectAreas([useComposerStore.getState().areas.find((candidate) => candidate.id === area.id) ?? area]);
    store.setAttributeFace('areas');
    return area;
};
